import { useEffect, useCallback } from 'react';

interface UseScrollToTopOptions {
  /**
   * Whether to use smooth scrolling (default: true)
   */
  smooth?: boolean;
  /**
   * Custom scroll behavior
   */
  behavior?: ScrollBehavior;
  /**
   * Custom top position (default: 0)
   */
  top?: number;
  /**
   * Custom left position (default: 0)
   */
  left?: number;
  /**
   * Whether to scroll immediately when hook is called (default: false)
   */
  scrollOnMount?: boolean;
}

/**
 * Custom hook for scrolling to top of the page
 * Can be used in individual components for more granular control
 * 
 * @param options - Configuration options for scrolling behavior
 * @returns Object with scrollToTop function
 */
export const useScrollToTop = (options: UseScrollToTopOptions = {}) => {
  const {
    smooth = true,
    behavior = 'smooth',
    top = 0,
    left = 0,
    scrollOnMount = false
  } = options;

  const scrollToTop = useCallback(() => {
    try {
      window.scrollTo({
        top,
        left,
        behavior: smooth ? behavior : 'auto'
      });
    } catch (error) {
      // Fallback for browsers that don't support smooth scrolling
      console.warn('Smooth scrolling not supported, using instant scroll');
      window.scrollTo(top, left);
    }
  }, [smooth, behavior, top, left]);

  // Scroll to top on mount if requested
  useEffect(() => {
    if (scrollOnMount) {
      const timeoutId = setTimeout(scrollToTop, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [scrollOnMount, scrollToTop]);

  return { scrollToTop };
};

export default useScrollToTop; 